import { type Nullable } from '../../api/types.ts';
import { getTimeZoneDateParts } from '../../assets/helpers/utils/time-zone-date.ts';

export type TooltipFormatterArgs = {
  value: number | null;
  axisValue: string;
  timeLabel: string;
  dataIndex: number;
};

type AxisDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

export const pad2 = (n: number) => String(n).padStart(2, '0');

const parseAxisKey = (value: string): Nullable<AxisDateParts> => {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);

  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] ?? 0),
    minute: Number(match[5] ?? 0),
    second: Number(match[6] ?? 0),
  };
};

/** Builds a time-zone-aware axis key without reducing the source timestamp precision. */
export const formatLocalAxisKey = (d: string, timeZone?: Nullable<string>) => {
  const parts = getTimeZoneDateParts(d, timeZone);

  if (!parts) return d;

  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

export const formatTickDay = (d: string) => {
  const parts = parseAxisKey(d);

  return parts ? pad2(parts.day) : '';
};

export const formatTooltipDate = (d: string) => {
  const parts = parseAxisKey(d);

  if (!parts) return '';

  return `${pad2(parts.day)}.${pad2(parts.month)}.${parts.year}`;
};

export const formatTooltipTime = (d: string) => {
  const parts = parseAxisKey(d);

  if (!parts) return '';

  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  ).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const defaultTooltipFormatter = ({ value, timeLabel }: TooltipFormatterArgs) =>
  `${value ?? '—'} ${timeLabel}`;
